// Locate the local player's starting capital in a snapshot, so the camera
// can anchor on it instead of the tile centroid at match start.

import type { GameSnapshot, SceneNode } from '@aod/protocol';
import { cityOwnerId } from '../ownership.js';

/**
 * Walk the snapshot's scene for the first city node flagged `isCapital`
 * owned by `localPlayerIndex`. Returns the city node's transform
 * position, suitable for passing into `getCameraPosition` to frame the
 * initial camera shot.
 *
 * Returns null when no capital is visible — the caller falls back to the
 * tile centroid so the camera still has a sensible focus (avoid the
 * "stares at the origin" bug if the snapshot is partial).
 */
export function findCapitalFocus(
  snapshot: GameSnapshot,
  localPlayerIndex: number,
): [number, number, number] | null {
  const all = snapshot.scene.children ?? [];
  for (const node of all) {
    if (node.type !== 'city') continue;
    const props = node.props as Record<string, unknown> | undefined;
    if (props?.isCapital !== true) continue;
    if (cityOwnerId(props) !== localPlayerIndex) continue;
    const pos = node.transform.position;
    return [pos[0], pos[1], pos[2]];
  }
  return null;
}

/**
 * Convenience for tests and renders that have already pulled the city list.
 * Keeps the snapshot scan in one place so the rule ("own capital flag wins")
 * is exercised once.
 */
export function findCapitalAmongCities(
  cities: ReadonlyArray<SceneNode>,
  localPlayerIndex: number,
): [number, number, number] | null {
  for (const node of cities) {
    if (node.type !== 'city') continue;
    const props = node.props as Record<string, unknown> | undefined;
    if (props?.isCapital !== true) continue;
    if (cityOwnerId(props) !== localPlayerIndex) continue;
    const pos = node.transform.position;
    return [pos[0], pos[1], pos[2]];
  }
  return null;
}
